import React, { useEffect, useState } from 'react';
import api from './api';
import TaskModal from './TaskModal';
import { STATUS_LABELS, STATUS_BADGE_CLASS, PRIORITY_BADGE_CLASS, userDisplayName, taskCode, formatDate, isOverdue } from './helpers';

function MyTasks({ users, currentUser, onOpenProject }) {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openTask, setOpenTask] = useState(undefined);
  const [personFilter, setPersonFilter] = useState(currentUser.id);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/tasks'), api.get('/projects')])
      .then(([tasksRes, projectsRes]) => {
        // Les projets archivés ne sont plus renvoyés : leurs tâches sortent
        // aussi de « Mes tâches ».
        const visibles = new Set(projectsRes.data.map((p) => p.id));
        setTasks(tasksRes.data.filter((t) => visibles.has(t.project_id)));
        setProjects(projectsRes.data);
      })
      .catch(() => setError('Impossible de charger les tâches.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Une personne voit les tâches dont elle est responsable, mais aussi celles
  // qui lui ont été déléguées : ce sont bien elles qu'elle doit réaliser.
  const idFiltre = parseInt(personFilter);
  const myTasks = tasks.filter(
    (t) => (t.assigned_to === idFiltre || t.delegated_to === idFiltre) && t.status !== 'Done'
  );
  const projectName = (id) => projects.find((p) => p.id === id)?.name || `Projet #${id}`;
  const projectOf = (task) => projects.find((p) => p.id === task.project_id);

  const handleTaskSaved = () => {
    setOpenTask(undefined);
    load();
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="fade-in">
      <div className="page-header-row">
        <div className="filter-pills">
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginRight: 4, alignSelf: 'center' }}>
            Voir les tâches de :
          </span>
          {users.map((u) => (
            <button
              key={u.id}
              className={`filter-pill ${parseInt(personFilter) === u.id ? 'active' : ''}`}
              onClick={() => setPersonFilter(u.id)}
            >
              {userDisplayName(u)}
            </button>
          ))}
        </div>
      </div>

      {myTasks.length === 0 && <div className="empty-state">Aucune tâche en attente pour cette personne. 🎉</div>}

      {myTasks.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Tâche</th>
                <th>Mon rôle</th>
                <th>Projet</th>
                <th>Statut</th>
                <th>Priorité</th>
                <th>Échéance</th>
              </tr>
            </thead>
            <tbody>
              {myTasks.map((task) => (
                <tr key={task.id} className="clickable" onClick={() => setOpenTask(task)}>
                  <td>{taskCode(task.id)}</td>
                  <td>{task.name}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {task.assigned_to === idFiltre ? (
                      <span className="badge badge-active">Responsable</span>
                    ) : (
                      <span className="badge badge-review" title={`Responsable : ${userDisplayName(users.find((u) => u.id === task.assigned_to))}`}>
                        Déléguée
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      style={{ color: 'var(--secondary)', cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); const p = projectOf(task); if (p) onOpenProject(p); }}
                    >
                      {projectName(task.project_id)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${STATUS_BADGE_CLASS[task.status] || 'badge-todo'}`}>
                      {STATUS_LABELS[task.status] || task.status}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${PRIORITY_BADGE_CLASS[task.priority] || 'badge-priority-medium'}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td><span className={isOverdue(task) ? 'date-retard' : ''}>{formatDate(task.due_date)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openTask !== undefined && (
        <TaskModal
          task={openTask}
          projectId={openTask?.project_id}
          users={users}
          currentUser={currentUser}
          onClose={() => setOpenTask(undefined)}
          onSaved={handleTaskSaved}
          onDeleted={handleTaskSaved}
        />
      )}
    </div>
  );
}

export default MyTasks;
